import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import s from './AboutApp.module.css';
import { API_URL } from '../config';
import { apiFetch } from '../utils/auth';
import { useRoleActions } from '../context/RoleContext';
import { ReactComponent as AppLogo } from '../icons/app_black_logo.svg';
import { ReactComponent as LeaveChatIcon } from '../icons/leave_chat_icon.svg';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import Link from '@tiptap/extension-link';
import { TextStyle, FontSize, Color } from '@tiptap/extension-text-style';
import DOMPurify from 'dompurify';
import { HexColorPicker } from 'react-colorful';

// Чтение/запись только через серверные ручки (без локального fallback)
async function fetchAbout() {
  const r = await apiFetch(`${API_URL}/side_pannel_actions/about`, { cache: 'no-store' });
  if (!r.ok) throw new Error('Не удалось загрузить текст');
  return await r.text();
}
async function saveAbout(text) {
  const r = await apiFetch(`${API_URL}/side_pannel_actions/about`, {
    method: 'PUT',
    headers: { 'Content-Type': 'text/plain; charset=utf-8' },
    body: text ?? '',
  });
  if (!r.ok) throw new Error('Не удалось сохранить текст');
  return true;
}

function escapeHtml(input) {
  return String(input || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
}

function isHtmlLike(input) {
  return /<\/?[a-z][\s\S]*>/i.test(String(input || ''));
}

function plainTextToHtml(input) {
  const lines = String(input || '').split('\n');
  const html = lines
    .map((line) => {
      if (!line.trim()) return '<p><br></p>';
      return `<p>${escapeHtml(line)}</p>`;
    })
    .join('');
  return html || '<p><br></p>';
}

export default function AboutApp() {
  const navigate = useNavigate();
  const { hasPermission } = useRoleActions();
  const canEditAbout = hasPermission('about.edit');

  const [text, setText] = useState('');
  const [editing, setEditing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [customColor, setCustomColor] = useState('#BD23DB');
  const pickerRef = useRef(null);

  const editorInitialContent = useMemo(
    () => (isHtmlLike(text) ? text : plainTextToHtml(text)),
    [text]
  );

  const promptAndSetLink = (editorInstance) => {
    if (!editorInstance) return;
    const url = window.prompt('Вставьте ссылку:');
    if (url === null) return;
    if (url === '') {
      editorInstance.chain().focus().unsetLink().run();
      return;
    }
    editorInstance
      .chain()
      .focus()
      .extendMarkRange('link')
      .setLink({ href: url, target: '_blank', rel: 'noopener nofollow' })
      .run();
  };

  const editor = useEditor({
    editable: editing,
    extensions: [
      StarterKit,
      Underline,
      Link.configure({ openOnClick: false }),
      TextStyle,
      FontSize,
      Color,
    ],
    content: editorInitialContent,
    editorProps: {
      attributes: { class: s.richEditor },
      handleKeyDown(view, event) {
        const isMod = event.ctrlKey || event.metaKey;
        if (!isMod) return false;
        const key = event.key.toLowerCase();
        const chain = view?.editor?.chain().focus();
        if (!chain) return false;

        if (key === 'b') {
          event.preventDefault();
          chain.toggleBold().run();
          return true;
        }
        if (key === 'i') {
          event.preventDefault();
          chain.toggleItalic().run();
          return true;
        }
        if (key === 'u') {
          event.preventDefault();
          chain.toggleUnderline().run();
          return true;
        }
        if (key === 'k') {
          event.preventDefault();
          promptAndSetLink(view.editor);
          return true;
        }
        return false;
      },
    },
    onUpdate({ editor: editorInstance }) {
      setText(editorInstance.getHTML());
    },
  });

  useEffect(() => {
    if (!editor) return;
    editor.setEditable(editing);
  }, [editor, editing]);

  useEffect(() => {
    if (!editor || !editing) return;
    const next = isHtmlLike(text) ? text : plainTextToHtml(text);
    if (editor.getHTML() !== next) {
      editor.commands.setContent(next, { emitUpdate: false });
    }
    // Подставляем контент только при входе в режим редактирования.
    // Иначе на каждом onUpdate собьется selection и курсор "улетит" в конец.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editor, editing]);

  useEffect(() => {
    if (!pickerOpen) return undefined;
    const onDocClick = (e) => {
      if (pickerRef.current && !pickerRef.current.contains(e.target)) {
        setPickerOpen(false);
      }
    };
    document.addEventListener('click', onDocClick);
    return () => document.removeEventListener('click', onDocClick);
  }, [pickerOpen]);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const t = await fetchAbout();
        setText(t || '');
      } catch {
        setText('');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const onSave = async () => {
    setSaving(true);
    try {
      const safeContent = DOMPurify.sanitize(text, {
        ALLOWED_TAGS: ['p', 'br', 'b', 'strong', 'i', 'em', 'u', 's', 'strike', 'ul', 'ol', 'li', 'a', 'span'],
        ALLOWED_ATTR: ['href', 'target', 'rel', 'style'],
      });
      await saveAbout(safeContent);
      setEditing(false);
    } finally {
      setSaving(false);
    }
  };

  const FONT_MIN = 12;
  const FONT_MAX = 35;
  const FONT_STEP = 2;

  const getCurrentFontSizePx = () => {
    const v = editor?.getAttributes('textStyle')?.fontSize || '';
    const num = parseInt(String(v).replace('px', ''), 10);
    return Number.isFinite(num) ? num : 16;
  };

  const applyFontSize = (px) => {
    if (!editor) return;
    const clamped = Math.max(FONT_MIN, Math.min(FONT_MAX, px));
    editor.chain().focus().setFontSize(`${clamped}px`).run();
  };

  const clearTextColor = () => editor?.chain().focus().unsetColor().run();
  const onPickColor = (hex) => {
    setCustomColor(hex);
    editor?.chain().focus().setColor(hex).run();
  };

  const safeHtmlForView = useMemo(() => {
    const html = isHtmlLike(text) ? text : plainTextToHtml(text);
    return DOMPurify.sanitize(html, {
      ALLOWED_TAGS: ['p', 'br', 'b', 'strong', 'i', 'em', 'u', 's', 'strike', 'ul', 'ol', 'li', 'a', 'span'],
      ALLOWED_ATTR: ['href', 'target', 'rel', 'style'],
    });
  }, [text]);

  return (
    <div className={s.page}>
      <header className={s.topbar}>
        <button className={s.backBtn} onClick={() => navigate(-1)} aria-label="Назад">
          <LeaveChatIcon />
        </button>
        <div className={s.brand}>
          <AppLogo aria-label="Ранги" style={{ width: 140, height: 'auto' }} />
        </div>
      </header>

      <h1 className={s.title}>Задачи 1C</h1>

      {loading ? (
        <div className={s.loading}>Загрузка…</div>
      ) : (
        <div className={s.content}>
          {editing ? (
            <>
              <div className={s.editorToolbar}>
                <button
                  type="button"
                  className={editor?.isActive('bold') ? s.isActive : ''}
                  onMouseDown={(e) => { e.preventDefault(); editor?.chain().focus().toggleBold().run(); }}
                  title="Жирный (Ctrl/Cmd+B)"
                ><b>B</b></button>

                <button
                  type="button"
                  className={editor?.isActive('italic') ? s.isActive : ''}
                  onMouseDown={(e) => { e.preventDefault(); editor?.chain().focus().toggleItalic().run(); }}
                  title="Курсив (Ctrl/Cmd+I)"
                ><i>I</i></button>

                <button
                  type="button"
                  className={editor?.isActive('underline') ? s.isActive : ''}
                  onMouseDown={(e) => { e.preventDefault(); editor?.chain().focus().toggleUnderline().run(); }}
                  title="Подчёркнутый (Ctrl/Cmd+U)"
                ><u>U</u></button>

                <button
                  type="button"
                  className={editor?.isActive('strike') ? s.isActive : ''}
                  onMouseDown={(e) => { e.preventDefault(); editor?.chain().focus().toggleStrike().run(); }}
                  title="Зачёркнутый"
                ><s>S</s></button>

                <button
                  type="button"
                  onMouseDown={(e) => { e.preventDefault(); editor?.chain().focus().undo().run(); }}
                  title="Отменить"
                >↶</button>

                <button
                  type="button"
                  onMouseDown={(e) => { e.preventDefault(); editor?.chain().focus().redo().run(); }}
                  title="Повторить"
                >↷</button>

                <button
                  type="button"
                  onMouseDown={(e) => { e.preventDefault(); promptAndSetLink(editor); }}
                  title="Вставить ссылку (Ctrl/Cmd+K)"
                >🔗</button>

                <button
                  type="button"
                  className={editor?.isActive('textStyle') ? s.isActive : ''}
                  onMouseDown={(e) => { e.preventDefault(); applyFontSize(getCurrentFontSizePx() - FONT_STEP); }}
                  title="Уменьшить размер шрифта"
                >Ａ−</button>

                <button
                  type="button"
                  className={editor?.isActive('textStyle') ? s.isActive : ''}
                  onMouseDown={(e) => { e.preventDefault(); applyFontSize(getCurrentFontSizePx() + FONT_STEP); }}
                  title="Увеличить размер шрифта"
                >Ａ+</button>

                <button
                  type="button"
                  onMouseDown={(e) => { e.preventDefault(); clearTextColor(); }}
                  title="Сбросить цвет"
                >✕</button>

                <div ref={pickerRef} className={s.colorPickerWrap}>
                  <span
                    role="button"
                    tabIndex={0}
                    onMouseDown={(e) => { e.preventDefault(); setPickerOpen((v) => !v); }}
                    title="Выбрать цвет"
                    className={s.paletteTrigger}
                  >🎨</span>
                  {pickerOpen && (
                    <div className={s.colorPopover}>
                      <HexColorPicker color={customColor} onChange={onPickColor} />
                    </div>
                  )}
                </div>
              </div>

              <EditorContent editor={editor} />
            </>
          ) : (
            text
              ? <article className={s.article} dangerouslySetInnerHTML={{ __html: safeHtmlForView }} />
              : <article className={s.article}><p className={s.muted}>Текст пока не задан.</p></article>
          )}
        </div>
      )}

      {canEditAbout && !loading && (
        <>
          {!editing ? (
            <button
              className={s.fab}
              type="button"
              onClick={() => setEditing(true)}
              aria-label="Редактировать"
              title="Редактировать"
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                  d="M2.5 21.4998L8.04927 19.3655C8.40421 19.229 8.58168 19.1607 8.74772 19.0716C8.8952 18.9924 9.0358 18.901 9.16804 18.7984C9.31692 18.6829 9.45137 18.5484 9.72028 18.2795L21 6.99982C22.1046 5.89525 22.1046 4.10438 21 2.99981C19.8955 1.89525 18.1046 1.89524 17 2.99981L5.72028 14.2795C5.45138 14.5484 5.31692 14.6829 5.20139 14.8318C5.09877 14.964 5.0074 15.1046 4.92823 15.2521C4.83911 15.4181 4.77085 15.5956 4.63433 15.9506L2.5 21.4998ZM2.5 21.4998L4.55812 16.1488C4.7054 15.7659 4.77903 15.5744 4.90534 15.4867C5.01572 15.4101 5.1523 15.3811 5.2843 15.4063C5.43533 15.4351 5.58038 15.5802 5.87048 15.8703L8.12957 18.1294C8.41967 18.4195 8.56472 18.5645 8.59356 18.7155C8.61877 18.8475 8.58979 18.9841 8.51314 19.0945C8.42545 19.2208 8.23399 19.2944 7.85107 19.4417L2.5 21.4998Z"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
            </button>
          ) : (
            <div className={s.editActions}>
              <button className={s.btn} onClick={() => setEditing(false)} disabled={saving}>
                Отменить
              </button>
              <button className={s.btnPrimary} onClick={onSave} disabled={saving}>
                {saving ? 'Сохранение…' : 'Сохранить'}
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
